import { useCallback, useEffect, useState } from "react";
import { api, API, idr } from "../lib/api";
import { Wallet, TrendingUp, TrendingDown, Users, Download, Lock, Send, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCabangOptions } from "../lib/cabang";
import { MonthPicker } from "@/components/ui/date-picker";
import { bulanIniISO } from "@/lib/tanggal";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

export default function RekapPayroll() {
  const { user } = useAuth();
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const canClose = user?.role !== "viewer";
  const [periode, setPeriode] = useState(bulanIniISO());
  const [branch, setBranch] = useState("");
  const [data, setData] = useState(null);
  const [closing, setClosing] = useState(false);
  // Urutan bawaan (per karyawan) dipertahankan sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState();

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (periode) params.set("periode", periode);
    if (branch) params.set("branch", branch);
    api.get(`/payroll/rekap?${params}`).then(r => setData(r.data));
  }, [periode, branch]);
  useEffect(() => { load(); }, [load]);

  const closePayroll = async () => {
    if (!confirm(`Close payroll periode ${periode}${branch ? " (" + branch + ")" : ""}? Setiap karyawan akan menerima slip gaji via WhatsApp.`)) return;
    setClosing(true);
    try {
      const { data: r } = await api.post("/payroll/close", { periode, branch: branch || null });
      const configured = r.twilio_configured;
      toast.success(`${r.closed} slip closed. WA: ${r.sent} terkirim, ${r.no_phone} tanpa nomor, ${r.failed} gagal${!configured ? " (Twilio belum aktif — MOCKED)" : ""}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    setClosing(false);
  };

  const downloadPdf = async (slipId, nama) => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/payroll/slip/${slipId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `SlipGaji_${nama}_${periode}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Payroll</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Rekap Payroll Bulanan</h1>
        <p className="mt-2 text-sm text-slate-500">Ringkasan seluruh slip gaji per periode dan cabang.</p>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periode</span>
          <MonthPicker value={periode} onChange={setPeriode}
            data-testid="rekap-periode"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Branch</span>
          <select value={branch} onChange={e => setBranch(e.target.value)}
            data-testid="rekap-branch"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
        </label>
        <button data-testid="btn-export-excel"
          onClick={async () => {
            const params = new URLSearchParams();
            if (periode) params.set("periode", periode);
            if (branch) params.set("branch", branch);
            const token = localStorage.getItem("hris_token");
            const res = await fetch(`${API}/payroll/rekap/export?${params}`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `Rekap_Payroll_${periode || "all"}${branch ? "_" + branch : ""}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
          }}
          className="ml-auto inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors">
          <Download className="w-4 h-4" /> Download Excel
        </button>
        {canClose && data && data.count > 0 && (
          <button data-testid="btn-close-payroll" onClick={closePayroll} disabled={closing}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white text-sm font-medium transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> {closing ? "Mengirim..." : "Close & Kirim Slip"}
          </button>
        )}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
            <Kpi icon={Users} label="Slip Diterbitkan" value={data.count} tint="emerald" delay={0} />
            <Kpi icon={TrendingUp} label="Total Bruto" value={idr(data.total_bruto)} tint="sky" delay={0.05} />
            <Kpi icon={TrendingDown} label="Total Potongan" value={idr(data.total_potongan)} tint="rose" delay={0.1} />
            <Kpi icon={Wallet} label="Total THP" value={idr(data.total_thp)} tint="amber" delay={0.15} />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <SortTh label="NIK" sortKeyName="employee_nik" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rekap-sort-nik" />
                  <SortTh label="Nama" sortKeyName="employee_nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rekap-sort-nama" />
                  <SortTh label="Branch" sortKeyName="branch" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rekap-sort-branch" />
                  <SortTh label="Grade" sortKeyName="grade_jabatan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="rekap-sort-grade" />
                  <SortTh label="Bruto" sortKeyName="bruto" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="rekap-sort-bruto" />
                  <SortTh label="Potongan" sortKeyName="potongan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="rekap-sort-potongan" />
                  <SortTh label="THP" sortKeyName="thp" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="rekap-sort-thp" />
                  <th className="text-center py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {sortRows(data.slips, sortKey, sortDir, {
                  bruto: (s) => s.calc.total_bruto, potongan: (s) => s.calc.total_potongan, thp: (s) => s.calc.thp,
                }).map(s => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 font-mono text-xs text-slate-600">{s.employee_nik}</td>
                    <td className="py-3 px-4 font-medium text-slate-900">{s.employee_nama}</td>
                    <td className="py-3 px-4 text-slate-600">{s.branch}</td>
                    <td className="py-3 px-4 text-slate-500 text-xs">{s.grade_jabatan}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs">{idr(s.calc.total_bruto)}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs text-rose-700">{idr(s.calc.total_potongan)}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs font-semibold text-emerald-700">{idr(s.calc.thp)}</td>
                    <td className="py-3 px-4 text-center">
                      {s.closed_at ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"><Lock className="w-3 h-3" /> Closed</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Draft</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => downloadPdf(s.id, s.employee_nama)} data-testid={`btn-pdf-${s.id}`}
                        title="Download PDF"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900 transition-colors">
                        <FileText className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {data.slips.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-12 text-sm text-slate-500">
                    Belum ada slip gaji untuk periode ini. Buat slip di menu <span className="font-medium">Payroll / Slip Gaji</span>.
                  </td></tr>
                )}
              </tbody>
              {data.slips.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-50 font-semibold text-slate-900">
                    <td colSpan={4} className="py-3 px-4">Total {data.count} slip</td>
                    <td className="py-3 px-4 text-right font-mono">{idr(data.total_bruto)}</td>
                    <td className="py-3 px-4 text-right font-mono text-rose-700">{idr(data.total_potongan)}</td>
                    <td className="py-3 px-4 text-right font-mono text-emerald-700" data-testid="rekap-total-thp">{idr(data.total_thp)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint = "emerald", delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-4`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-xl font-bold text-slate-900">{value}</p>
    </motion.div>
  );
}
